import { Button, Space, Input, InputRef, Spin } from "antd"
import { useRef, useState } from "react"
import { ColumnType, FilterDropdownProps } from "antd/es/table/interface"
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import Highlighter from "react-highlight-words"
import { HIGHLIGHT_COLOR } from "../constants/filter.constants"

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & string]: ObjectType[Key] extends object
    ? ObjectType[Key] extends Array<any>
      ? Key
      : `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`
}[keyof ObjectType & string]

interface UseSmartSearchColumnProps<T extends object> {
  dataIndex: NestedKeyOf<T>
  data?: T[]
  setData?: (data: T[]) => void
  fetchRemoteData?: (searchText: string) => Promise<T[]>
}

const getNestedValue = <T extends object>(obj: T, key: string): any => {
  return key.split(".").reduce((acc: any, k) => acc?.[k], obj)
}

const useSearchColumn = <T extends object>({
  dataIndex,
  data,
  setData,
  fetchRemoteData,
}: UseSmartSearchColumnProps<T>): Partial<ColumnType<T>> => {
  const [searchText, setSearchText] = useState<string>("")
  const [searchedColumn, setSearchedColumn] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const searchInput = useRef<InputRef>(null)

  const handleSearch = async (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: string
  ) => {
    const value = selectedKeys[0]
    if (!value) {
      return
    }

    confirm()
    setSearchedColumn(dataIndex)
    setSearchText(value)

    if (data && setData) {
      const foundLocally = data.some((record) =>
        String(getNestedValue(record, dataIndex) ?? "")
          .toLowerCase()
          .includes(value.toLowerCase())
      )

      if (!foundLocally && fetchRemoteData) {
        setIsLoading(true)
        const remoteResults = await fetchRemoteData(value)
        if (remoteResults && remoteResults.length > 0) {
          setData([...data, ...remoteResults])
        }
        setIsLoading(false)
      }
    }
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText("")
  }

  return {
    filterDropdown: (props: FilterDropdownProps) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters, close } = props

      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                confirm({ closeDropdown: false })
                setSearchText((selectedKeys as string[])[0])
                setSearchedColumn(dataIndex)
              }}
            >
              Filter
            </Button>
            <Button type="link" size="small" onClick={close}>
              Close
            </Button>
          </Space>
        </div>
      )
    },
    filterIcon: (filtered: boolean) =>
      isLoading ? (
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      ) : (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    onFilter: (value, record) => {
      if (isLoading) {
        return true
      }

      return String(getNestedValue(record, dataIndex) ?? "")
        .toLowerCase()
        .includes((value as string).toLowerCase())
    },
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100)
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: HIGHLIGHT_COLOR, padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? String(text) : ""}
        />
      ) : (
        text
      ),
  }
}

export default useSearchColumn
